import { LeadThread } from "./leadApi";

/* ── Design tokens — Step Up B13 brand ────────────────────────────────────
 * These names are legacy (NAVY / GOLD) from the DCC fork; kept only so
 * downstream files continue to compile. The VALUES come from Step Up's
 * canonical B13 palette (frontend/src/theme/b13Tokens.ts) so anywhere
 * these constants are consumed inherits the same brand identity as the
 * rest of the application.
 *
 *   NAVY → B13.ink    (#16233A — Step Up primary ink)
 *   GOLD → B13.accent (#7C4DFF — Step Up secondary accent, violet)
 *
 * Do NOT re-hardcode DCC values here — see `leadTheme.ts` for the full
 * migration story. */
import { B13 } from "../../theme/b13Tokens";
export const NAVY = B13.ink;
export const GOLD = B13.accent;

/* Runtime constant — safe to compute once at module load.
   Local date (NOT toISOString — that gives UTC, which would mark "today"
   as tomorrow after ~8 PM EDT and miscolour follow-up-overdue chips). */
export const todayStr = (() => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
})();

/* ── Avatar colour hash ─────────────────────────────────────────────────── */
const AVATAR_COLORS = [
  { bg: "#6366f1", fg: "#fff" }, { bg: "#ec4899", fg: "#fff" },
  { bg: "#f59e0b", fg: "#fff" }, { bg: "#10b981", fg: "#fff" },
  { bg: "#3b82f6", fg: "#fff" }, { bg: "#8b5cf6", fg: "#fff" },
  { bg: "#ef4444", fg: "#fff" }, { bg: "#06b6d4", fg: "#fff" },
  { bg: "#f97316", fg: "#fff" }, { bg: "#84cc16", fg: "#fff" },
];

export function avatarColor(name?: string | null) {
  if (!name) return { bg: GOLD, fg: NAVY };
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

/* ── Safe date formatter ────────────────────────────────────────────────── */
export function safeFormatDate(
  d: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" },
): string {
  if (!d) return "—";
  // Strip time portion so we always parse as local midnight (avoids
  // timezone day-shift). Locale is en-US — en-CA renders "May 16, 2026"
  // identically when explicit options are passed but with a stray default
  // falls back to "2026-05-16" (ISO-style) which is the bug this helper
  // exists to prevent.
  const dateOnly = d.split("T")[0];
  const dt = new Date(dateOnly + "T00:00:00");
  return isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("en-US", opts);
}

/**
 * Canonical formatter for a lead's event date — use this everywhere an
 * event date is shown (drawer, table, pipeline cards, detail page, etc.).
 *
 * `event_date` is a date-only field; the time in the API's ISO string
 * (e.g. "2026-05-16T04:00:00.000000Z") is just a midnight/timezone
 * artifact. We strip it and parse at local midnight so the calendar date
 * is shown verbatim — "May 16, 2026" — with no timezone day-shift.
 * Returns "Not specified" when the value is missing or invalid.
 */
export function formatEventDate(d: string | null | undefined): string {
  if (!d) return "Not specified";
  const dateOnly = String(d).split("T")[0];
  const dt = new Date(dateOnly + "T00:00:00");
  return isNaN(dt.getTime())
    ? "Not specified"
    : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Canonical formatter for a lead's budget — the single source of truth for
 * how budget is displayed across every lead surface (drawer, detail page,
 * client profile, etc.). A budget only counts as real when it was explicitly
 * marked known AND a positive amount was entered. Anything else — not known,
 * null, empty, zero, or a stray value left with budget_known = false — shows
 * "Not specified". Never falls back to a default amount.
 *
 * Accepts a minimal shape so it works for both LeadThread and ICrmLead.
 * (budget_value can arrive as a decimal string like "100.00".)
 */
export function formatLeadBudget(
  lead: { budget_known?: boolean | null; budget_value?: number | string | null },
): string {
  const num = lead.budget_value == null ? NaN : Number(lead.budget_value);
  if (!lead.budget_known || !Number.isFinite(num) || num <= 0) {
    return "Not specified";
  }
  return "$" + num.toLocaleString("en-US");
}

/* ── Source config ──────────────────────────────────────────────────────── */
export interface SourceMeta { icon: string; label: string; color: string; bg: string }

export const SOURCE_CFG: Record<string, SourceMeta> = {
  website:  { icon: "🌐", label: "Website",  color: "#0284c7", bg: "#e0f2fe" },
  email:    { icon: "📧", label: "Email",    color: "#7c3aed", bg: "#ede9fe" },
  phone:    { icon: "📞", label: "Phone",    color: "#d97706", bg: "#fef3c7" },
  walk_in:  { icon: "🚶", label: "Walk-in",  color: "#dc2626", bg: "#fee2e2" },
  referral: { icon: "🔗", label: "Referral", color: "#db2777", bg: "#fce7f3" },
  manual:   { icon: "⚙️", label: "Manual",   color: "#059669", bg: "#d1fae5" },
};

export const getSource = (src?: string | null): SourceMeta =>
  SOURCE_CFG[src ?? ""] ?? { icon: "❓", label: src ?? "Unknown", color: "#64748b", bg: "#f1f5f9" };

/* ── Smart Next Action ──────────────────────────────────────────────────── */
export interface NextActionDef {
  label: string;
  color: string;
  bg: string;
  urgent?: boolean;
}

export function getNextAction(lead: LeadThread): NextActionDef | null {
  const ageDays   = (Date.now() - new Date(lead.created_at).getTime()) / 86_400_000;
  const isOverdue = lead.follow_up_required && lead.follow_up_at && lead.follow_up_at < todayStr;

  if (isOverdue)
    return { label: "⚡ Urgent Follow-up",    color: "#fff",    bg: "#ef4444", urgent: true };
  if (lead.status === "new" && !lead.first_response_at && ageDays > 1)
    return { label: "⚠️ Contact Immediately", color: "#dc2626", bg: "#fee2e2", urgent: true };
  if (lead.status === "confirmed")
    return { label: "✅ No Action Needed",    color: "#16a34a", bg: "#dcfce7" };
  if (lead.status === "lost")
    return null;

  const MAP: Record<string, NextActionDef> = {
    new:               { label: "📞 Call Now",        color: "#dc2626", bg: "#fee2e2" },
    contacted:         { label: "💬 Follow Up",        color: "#7c3aed", bg: "#ede9fe" },
    negotiation:       { label: "📋 Send Quote",       color: "#d97706", bg: "#fef3c7" },
    ready_for_quote:   { label: "📄 Prepare Quote",    color: "#0d9488", bg: "#ccfbf1" },
    ready_for_booking: { label: "✅ Confirm Booking",  color: "#16a34a", bg: "#dcfce7" },
  };
  return MAP[lead.status] ?? null;
}
